import * as React from 'react';

import { cn } from '@/utils/tailwind';
import EvaSearchFill from '@/assets/icons/EvaSearchFill.svg?react';
import InputDeleteButton from '@/assets/icons/InputDeleteButton.svg?react';
import InputDeleteButtonDisabled from '@/assets/icons/InputDeleteButtonDisabled.svg?react';

interface InputBoxProps extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'size'> {
  leftIcon?: boolean;
  className?: string;
  type?: string;
  invalid?: boolean;
  deleteBtn?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputSize?: 'small' | 'medium'; // 이름 변경
  wrapperClassName?: string; // 추가된 prop, 래퍼 클래스 이름
}

function InputBox({
  leftIcon = false,
  invalid = false,
  deleteBtn = false,
  className,
  type,
  value: propValue,
  onChange: propOnChange,
  wrapperClassName,
  inputSize = 'medium', // 이름 변경 및 기본값 medium
  ...props
}: InputBoxProps) {
  // controlled input state
  const [value, setValue] = React.useState(propValue ?? '');

  // prop value가 바뀌면 내부 state도 동기화
  React.useEffect(() => {
    if (typeof propValue === 'string' && propValue !== value) {
      setValue(propValue);
    }
    // eslint-disable-next-line
  }, [propValue]);

  // input 변경 핸들러
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    propOnChange?.(e);
  };

  // 삭제 버튼 클릭 핸들러
  const handleDelete = () => {
    setValue('');
    propOnChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
  };

  // 사이즈별 클래스
  const sizeClasses = inputSize === 'small' ? 'h-[30px] Caption1M12' : 'h-[40px] Body5M14';

  // 아이콘 있을 때 왼쪽 패딩
  const leftPadding = leftIcon ? (inputSize === 'small' ? 'pl-[38px]' : 'pl-[38px]') : '';

  // 삭제 버튼 공간 확보
  const rightPadding = deleteBtn ? (inputSize === 'small' ? 'pr-[38px]' : 'pr-[38px]') : '';

  return (
    <div className={cn('relative flex items-center', wrapperClassName)}>
      {leftIcon && (
        <span
          className={cn(
            'pointer-events-none absolute',
            inputSize === 'small' ? 'left-[8px]' : 'left-[10px]',
          )}
        >
          <EvaSearchFill />
        </span>
      )}
      <input
        type={type}
        data-slot="input"
        value={value}
        onChange={handleChange}
        className={cn(
          sizeClasses,
          'px-[10px] py-[9px] text-gr-800 outline-none placeholder:text-gr-400',
          'file:text-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium',
          'selection:bg-point-400 selection:text-white',
          'rounded-[8px] border border-gr-300 bg-white',
          'flex w-full min-w-0',
          leftPadding,
          rightPadding,
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-gr-200 disabled:bg-gr-100 disabled:text-gr-500',
          'focus-visible:border-gr-400',
          invalid ? 'border-red-400 focus:border-red-400 focus-visible:border-red-400' : '',
          className,
        )}
        {...props}
      />
      {/* deleteBtn이 true */}
      {deleteBtn && (
        <button
          type="button"
          onClick={handleDelete}
          tabIndex={-1}
          className={cn(
            'absolute flex items-center justify-center',
            inputSize === 'small' ? 'right-[8px]' : 'right-[10px]',
            props.disabled && 'pointer-events-none cursor-not-allowed',
          )}
        >
          {props.disabled ? <InputDeleteButtonDisabled /> : <InputDeleteButton />}
        </button>
      )}
    </div>
  );
}

export { InputBox };
